import Column from "@components/column";
import Row from "@components/row";
import Typography from "@components/typography";
import { Helper } from "@utils/index";

export const getOrderColumns = (bucketUrl?: string, settings?: any) => [
  {
    key: "sn",
    title: "S.N",
    render: (_: any, i: number) => i + 1,
  },
  {
    key: "item-details",
    title: "Item Details",
    render: (item: any) => {
      const product = item.infos?.product || item.product || {};
      const liveProduct = item.product || {};

      // Get image with proper URL construction
      let image =
        product.images?.[0]?.image ||
        product.images?.[0] ||
        product.image ||
        liveProduct.images?.[0]?.image ||
        liveProduct.images?.[0] ||
        liveProduct.image;

      // If it's an object with image property, extract it
      if (typeof image === "object" && image?.image) {
        image = image.image;
      }

      // Build full URL if bucketUrl provided and image doesn't start with http
      if (image && bucketUrl && !image.startsWith("http")) {
        image = `${bucketUrl}/${image}`;
      } else if (!image) {
        image = "/placeholder.png";
      }

      const quantity = item.infos?.quantity || item.quantity || 1;
      const unitPrice = item.actualAmount || item.price || 0;
      const isAvailable = item.isAvailable !== false;
      const alternative = item.alternativeProduct;

      // Get variation info
      const variationId = item.infos?.variationId;
      const variations = product.variations || [];
      const selectedVariation = variations.find(
        (v: any) => String(v.id) === String(variationId),
      );

      // Get addons info
      const addons = product.addons || [];

      return (
        <Row styleRow={`gap:12px;align-items:center;${!isAvailable ? "opacity: 0.7;" : ""}`}>
          <div>
            <img
              src={image}
              alt={item.customName || product.name}
              width={50}
              height={50}
              style={{ objectFit: "cover", borderRadius: "6px" }}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/50x50?text=No+Img";
              }}
            />
          </div>
          <Column stylecolumn="gap:4px">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography size={14} weight={500}>
                {item.customName || product.name || "Unknown Product"}
              </Typography>
              {!isAvailable && (
                <span style={{ 
                  background: '#fee2e2', 
                  color: '#dc2626', 
                  fontSize: '10px', 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  fontWeight: 600
                }}>
                  Unavailable
                </span>
              )}
            </div>
            
            <Typography size={12} color="#666">
              {quantity} x {Helper.formatCurrency(unitPrice, settings)}
            </Typography>

            {/* Alternative Product Display */}
            {!isAvailable && alternative && (
              <div style={{ 
                marginTop: '4px', 
                padding: '6px 10px', 
                background: '#f0f9ff', 
                borderLeft: '3px solid #0ea5e9',
                borderRadius: '4px'
              }}>
                <Typography size={11} weight={600} color="#0369a1">
                  ALT: {alternative.name || alternative.customName}
                </Typography>
                <Typography size={10} color="#0c4a6e">
                  Price: {Helper.formatCurrency(alternative.price, settings)}
                </Typography>
              </div>
            )}

            {selectedVariation?.attributes && (
              <Typography size={11} color="#888">
                {Object.entries(selectedVariation.attributes)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(", ")}
              </Typography>
            )}
            {item.infos?.variation && !selectedVariation && (
              <Typography size={11} color="#888">
                Var: {item.infos.variation}
              </Typography>
            )}
            {addons.length > 0 && (
              <Typography size={10} color="#999">
                Addons: {addons.map((a: any) => a.name).join(", ")}
              </Typography>
            )}
            {product.activeDeal && (
              <div
                style={{
                  background: "#fee2e2",
                  color: "#ef4444",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  display: "inline-block",
                  fontWeight: 600,
                  width: "fit-content",
                  marginBottom: "4px",
                }}
              >
                Deal Applied: {product.activeDeal.dealName}
              </div>
            )}
            {product.prescriptionRequired && (
              <div
                style={{
                  background: "#dbeafe",
                  color: "#1e40af",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  display: "inline-block",
                  fontWeight: 600,
                  width: "fit-content",
                }}
              >
                Prescription Required
              </div>
            )}
          </Column>
        </Row>
      );
    },
  },
  {
    key: "item-price",
    title: "Unit Price",
    render: (item: any) => {
      const price = item.actualAmount || item.price || 0;
      return <span>{Helper.formatCurrency(price, settings)}</span>;
    },
  },
  {
    key: "tax",
    title: "Tax",
    render: (item: any) => (
      <span>{Helper.formatCurrency(item.tax ?? 0, settings)}</span>
    ),
  },
  {
    key: "item-discount",
    title: "Discount",
    render: (item: any) => (
      <span>
        {Helper.formatCurrency(
          item.discountAmount || item.itemDiscount || 0,
          settings,
        )}
      </span>
    ),
  },
  {
    key: "total-price",
    title: "Total",
    render: (item: any) => (
      <span style={{ fontWeight: 600 }}>
        {Helper.formatCurrency(
          item.totalAmount || item.totalPrice || 0,
          settings,
        )}
      </span>
    ),
  },
];
